import React, { useState, useEffect } from 'react';
import { Fab, Tooltip } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { 
  CharacterSheetInterface,
  RollPlus,
  RolledValue
} from "@dndModels";
import { 
  ABILITY_SCORES,
} from "@dndModels/Constants";
import { CritFail, CritSuccess, D10, D100_WHITE, D12, D20, D4, D6, D8 } from "@models/Images";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import ImageFromString from "@dndComponents/display/ImageFromString";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ButtonBox from "@components/input/buttons/ButtonBox";
import StringBox from "@components/input/strings/StringBox";
import ImageButtonBox from "@components/input/buttons/ImageButtonBox";

interface Props {
  char_interface?: CharacterSheetInterface;
}

const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CustomRolls({ 
  char_interface,
}: Props) {
  const [roll_string, setRollString] = useState("");
  const [rolls, setRolls] = useState<RolledValue[]>([]);
  const [open, setOpen] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderRolledValue = (rolled_value: RolledValue, index: number) => {
    const return_me: { render: any, total: number } = {
      total: 0,
      render: null
    };
    let total = 0; // rolled_value.flat;
    const bonuses = rolled_value.flat;
    const dr = rolled_value;
    if (dr.size === 1) {
      if (dr.count !== 0) {
        total += dr.count;
      }
      return_me.total = total;
      return_me.render = (
        <Grid key={index} container spacing={0} direction="row">
          <Grid
            size={{
              xs: 3
            }}></Grid>
          <Grid
            size={{
              xs: 3
            }}>
            { dr.count }
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            { total }
            <ImageFromString image={rolled_value.type} size={25} color="white" />
          </Grid>
        </Grid>
      );
    } else if (dr.count === 0) {
      return_me.total = total;
      return_me.render = (
        <Grid key={index} container spacing={0} direction="row">
          <Grid
            size={{
              xs: 3
            }}></Grid>
          <Grid
            size={{
              xs: 3
            }}>
            { dr.flat }
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            { total }
            <ImageFromString image={rolled_value.type} size={25} color="white" />
          </Grid>
        </Grid>
      );
    } else {
      let image = "";
      switch(+dr.size) {
        case 4: image = D4; break;
        case 6: image = D6; break;
        case 8: image = D8; break;
        case 10: image = D10; break;
        case 12: image = D12; break;
        case 20: image = D20; break;
        case 100: image = D100_WHITE; break;
      }
      let count = dr.count;
      let reverse = false;
      let joiner = " + ";
      if (count < 0) {
        count *= -1;
        reverse = true;
        joiner = " - ";
      }
      if (rolled_value.advantage || rolled_value.roll_values.filter(o => o.minrolled || o.rerolled).length > 0) {
        for (const roll_value of rolled_value.roll_values) {
          let value_for_crit = 0;
          if (rolled_value.advantage !== 0) {
            if (roll_value.main_values.length === 2) {
              value_for_crit = rolled_value.advantage > 0 ? Math.max(...roll_value.main_values) : Math.min(...roll_value.main_values);
              total += value_for_crit;
            }
          } else if (roll_value.rerolled) {
            if (roll_value.minrolled) {
              if (roll_value.main_values.length === 3) {
                value_for_crit = roll_value.main_values[2];
                total += roll_value.main_values[2];
              }
            } else {
              if (roll_value.main_values.length === 2) {
                value_for_crit = roll_value.main_values[1];
                total += roll_value.main_values[1];
              }
            }
          } else if (roll_value.minrolled) {
            if (roll_value.main_values.length === 2) {
              value_for_crit = roll_value.main_values[1];
              total += roll_value.main_values[1];
            }
          } else {
            if (roll_value.main_values.length > 0) {
              value_for_crit = roll_value.main_values[0];
              total += roll_value.main_values[0];
            }
          }
          if (dr.size === 20) {
            if (value_for_crit === 20) {
              image = CritSuccess;
            } else if (value_for_crit === 1) {
              image = CritFail;
            }
          }
        }
      } else {
        for (const roll_value of rolled_value.roll_values) {
          let value_for_crit = 0;
          if (roll_value.main_values.length > 0) {
            value_for_crit = roll_value.main_values[0];
            total += roll_value.main_values[0];
          }
          if (dr.size === 20) {
            if (value_for_crit === 20) {
              image = CritSuccess;
            } else if (value_for_crit === 1) {
              image = CritFail;
            }
          }
        }
      }
      total += bonuses;
      return_me.total = reverse ? total * -1 : total;
      return_me.render = (
        <Grid key={index} container spacing={0} direction="row">
          <Grid
            size={{
              xs: 3
            }}>
            { image !== "" && 
              <img src={image} alt={image} 
                style={{
                  // width: "40px",
                  height: "40px"
                }} 
              />
            }
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            { `${dr.count}d${dr.size}` }
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            { (rolled_value.advantage || rolled_value.roll_values.filter(o => o.minrolled || o.rerolled).length > 0) ?
              <Grid container spacing={0} direction="row">
                { rolled_value.roll_values.map((roll_value, key) => {
                  if (rolled_value.advantage !== 0) {
                    if (roll_value.main_values.length === 2) {
                      if (rolled_value.advantage > 0 && roll_value.main_values[0] >= roll_value.main_values[1]) {
                        return (
                          <Grid
                            key={key}
                            size={{
                              xs: 12
                            }}>
                            <span style={{ fontWeight: "bold", color: (rolled_value.advantage > 0 ? "green" : "red")}}>{ roll_value.main_values[0] }</span>, { roll_value.main_values[1] }
                          </Grid>
                        );
                      } else {
                        return (
                          <Grid
                            key={key}
                            size={{
                              xs: 12
                            }}>
                            { roll_value.main_values[0] }, <span style={{ fontWeight: "bold", color: (rolled_value.advantage > 0 ? "green" : "red")}}>{ roll_value.main_values[1] }</span>
                          </Grid>
                        );
                      }
                    }
                  } else if (roll_value.rerolled) {
                    if (roll_value.minrolled) {
                      if (roll_value.main_values.length === 3) {
                        return (
                          <Grid
                            key={key}
                            size={{
                              xs: 12
                            }}>
                            { roll_value.main_values[0] }(Reroll: { roll_value.main_values[1] }) <span style={{ fontWeight: "bold", color: "green" }}>Min Roll: { roll_value.main_values[2] }</span>
                          </Grid>
                        );
                      }
                    } else {
                      if (roll_value.main_values.length === 2) {
                        return (
                          <Grid
                            key={key}
                            size={{
                              xs: 12
                            }}>
                            { roll_value.main_values[0] } <span style={{ fontWeight: "bold", color: "green" }}>Reroll: { roll_value.main_values[1] }</span>
                          </Grid>
                        );
                      }
                    }
                  } else if (roll_value.minrolled) {
                    if (roll_value.main_values.length === 2) {
                      return (
                        <Grid
                          key={key}
                          size={{
                            xs: 12
                          }}>
                          { roll_value.main_values[0] } <span style={{ fontWeight: "bold", color: "green" }}>Min Roll: { roll_value.main_values[1] }</span>
                        </Grid>
                      );
                    }
                  } else {
                    if (roll_value.main_values.length > 0) {
                      return (
                        <Grid
                          key={key}
                          size={{
                            xs: 12
                          }}>
                          { roll_value.main_values[0] }
                        </Grid>
                      );
                    }
                  }
                  return null;
                })}
              </Grid>
            : rolled_value.roll_values.map(o => `${o.main_values[0]}`).join(joiner) }
            { common_util.addPlusMaybe(bonuses, true) }
            { (count > 1 || bonuses !== 0) && `= ${ total }` }
            <ImageFromString image={rolled_value.type} size={25} color="white" />
          </Grid>
        </Grid>
      );
    }
    return return_me;
  }

  const renderRolls = () => {
    let grand_total = 0;

    const roll_renders: any[] = [];
    // debugger;
    // let bonus = 0;
    for (let i = 0; i < rolls.length; ++i) {
      const rolled_value = rolls[i];
      
      if (rolled_value.size === 1) {
        grand_total += rolled_value.count;
        // bonus += rolled_value.count;
      } else if (rolled_value.size === 0) {
        if (rolled_value.flat !== 0) {
          grand_total += rolled_value.flat;
          roll_renders.push(
            <Grid key={i} container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 6
                }}>
                &nbsp;
              </Grid>
              <Grid
                size={{
                  xs: 6
                }}>
                { rolled_value.flat }
              </Grid>
            </Grid>
          );
        }
      } else {
        const roll_render = renderRolledValue(rolled_value, i);
        roll_renders.push(roll_render.render);
        grand_total += roll_render.total;
      }
    }
    if (roll_renders.length > 0) {
      roll_renders.push(
        <Grid key="total" container spacing={0} direction="row">
          <Grid
            size={{
              xs: 6
            }}>
            Grand Total =
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            { grand_total }
          </Grid>
        </Grid>
      );
    }
    return roll_renders;
  }

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={ open } 
          onClose={() => {
            setOpen(false);
            setRollString("");
            setRolls([]);
          }}>
          { open && 
            <Grid container spacing={0} direction="row">
              <Grid
                className="header centered"
                size={{
                  xs: 12
                }}>Custom Rolls</Grid>
              <Grid
                size={{
                  xs: 12
                }}>
                <StringBox
                  name="Roll String"
                  value={roll_string}
                  onBlur={setRollString}
                />
              </Grid>
              <Grid
                className="centered"
                size={{
                  xs: 4
                }}>
                <ImageButtonBox
                  name=''
                  image_url={D4}
                  width={40}
                  small
                  onClick={() => {
                    let new_roll_string = roll_string;
                    const pieces = new_roll_string.split("+");
                    const piece = pieces.find(o => o.includes("d4"));
                    if (piece) {
                      const roll_pieces = piece.split("d");
                      let count = +roll_pieces[0];
                      count++;
                      new_roll_string = new_roll_string.replace(piece, `${count}d4`);
                    } else {
                      new_roll_string += new_roll_string.length === 0 ? "1d4" : "+1d4";
                    }
                    setRollString(new_roll_string);
                  }}
                />
              </Grid>
              <Grid
                className="centered"
                size={{
                  xs: 4
                }}>
                <ImageButtonBox
                  name=''
                  image_url={D6}
                  width={40}
                  small
                  onClick={() => {
                    let new_roll_string = roll_string;
                    const pieces = new_roll_string.split("+");
                    const piece = pieces.find(o => o.includes("d6"));
                    if (piece) {
                      const roll_pieces = piece.split("d");
                      let count = +roll_pieces[0];
                      count++;
                      new_roll_string = new_roll_string.replace(piece, `${count}d6`);
                    } else {
                      new_roll_string += new_roll_string.length === 0 ? "1d6" : "+1d6";
                    }
                    setRollString(new_roll_string);
                  }}
                />
              </Grid>
              <Grid
                className="centered"
                size={{
                  xs: 4
                }}>
                <ImageButtonBox
                  name=''
                  image_url={D8}
                  width={40}
                  small
                  onClick={() => {
                    let new_roll_string = roll_string;
                    const pieces = new_roll_string.split("+");
                    const piece = pieces.find(o => o.includes("d8"));
                    if (piece) {
                      const roll_pieces = piece.split("d");
                      let count = +roll_pieces[0];
                      count++;
                      new_roll_string = new_roll_string.replace(piece, `${count}d8`);
                    } else {
                      new_roll_string += new_roll_string.length === 0 ? "1d8" : "+1d8";
                    }
                    setRollString(new_roll_string);
                  }}
                />
              </Grid>
              <Grid
                className="centered"
                size={{
                  xs: 3
                }}>
                <ImageButtonBox
                  name=''
                  image_url={D10}
                  width={40}
                  small
                  onClick={() => {
                    let new_roll_string = roll_string;
                    const pieces = new_roll_string.split("+");
                    const piece = pieces.find(o => o.includes("d10"));
                    if (piece) {
                      const roll_pieces = piece.split("d");
                      let count = +roll_pieces[0];
                      count++;
                      new_roll_string = new_roll_string.replace(piece, `${count}d10`);
                    } else {
                      new_roll_string += new_roll_string.length === 0 ? "1d10" : "+1d10";
                    }
                    setRollString(new_roll_string);
                  }}
                />
              </Grid>
              <Grid
                className="centered"
                size={{
                  xs: 3
                }}>
                <ImageButtonBox
                  name=''
                  image_url={D12}
                  width={40}
                  small
                  onClick={() => {
                    let new_roll_string = roll_string;
                    const pieces = new_roll_string.split("+");
                    const piece = pieces.find(o => o.includes("d12"));
                    if (piece) {
                      const roll_pieces = piece.split("d");
                      let count = +roll_pieces[0];
                      count++;
                      new_roll_string = new_roll_string.replace(piece, `${count}d12`);
                    } else {
                      new_roll_string += new_roll_string.length === 0 ? "1d12" : "+1d12";
                    }
                    setRollString(new_roll_string);
                  }}
                />
              </Grid>
              <Grid
                className="centered"
                size={{
                  xs: 3
                }}>
                <ImageButtonBox
                  name=''
                  image_url={D20}
                  width={40}
                  small
                  onClick={() => {
                    let new_roll_string = roll_string;
                    const pieces = new_roll_string.split("+");
                    const piece = pieces.find(o => o.includes("d20"));
                    if (piece) {
                      const roll_pieces = piece.split("d");
                      let count = +roll_pieces[0];
                      count++;
                      new_roll_string = new_roll_string.replace(piece, `${count}d20`);
                    } else {
                      new_roll_string += new_roll_string.length === 0 ? "1d20" : "+1d20";
                    }
                    setRollString(new_roll_string);
                  }}
                />
              </Grid>
              <Grid
                className="centered"
                size={{
                  xs: 3
                }}>
                <ImageButtonBox
                  name=''
                  image_url={D100_WHITE}
                  height={40}
                  small
                  onClick={() => {
                    let new_roll_string = roll_string;
                    const pieces = new_roll_string.split("+");
                    const piece = pieces.find(o => o.includes("d100"));
                    if (piece) {
                      const roll_pieces = piece.split("d");
                      let count = +roll_pieces[0];
                      count++;
                      new_roll_string = new_roll_string.replace(piece, `${count}d100`);
                    } else {
                      new_roll_string += new_roll_string.length === 0 ? "1d100" : "+1d100";
                    }
                    setRollString(new_roll_string);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name="Roll"
                  onClick={() => {
                    const pieces = roll_string.split("+");
                    const new_rolls: RollPlus[] = [];
                    const getNumber = (str: string) => {
                      if (ABILITY_SCORES.includes(str) && char_interface) {
                        return char_interface.mind.current_ability_scores.getModifier(str);
                      } else {
                        const num = +str;
                        if (isNaN(num)) {
                          console.log(`${str} is not a number.`);
                          return 0;
                        } else {
                          return num;
                        }
                      }
                    }
                    for (const piece of pieces) {
                      const new_roll = new RollPlus();
                      if (piece.includes("d")) {
                        const roll_pieces = piece.split("d");
                        new_roll.count = getNumber(roll_pieces[0]);
                        new_roll.size = getNumber(roll_pieces[1]);
                      } else {
                        new_roll.flat = getNumber(piece);
                      }
                      new_rolls.push(new_roll);
                    }
                    setRolls(char_util.rollOther({ char_interface, rolls: new_rolls, type: "Custom" }));
                    setReloading(true);
                  }}
                />
              </Grid>
              { !reloading && renderRolls() }
            </Grid>
          }
        </ModalOrDrawer>
      </>
    );
  }

  return <></>
  // return (
  //   <div style={{ 
  //     position: "fixed",
  //     zIndex: 999,
  //     left: 20,
  //     bottom: 10
  //   }}>
  //     <Tooltip title={`Custom Dice Rolls`}>
  //       <Fab size="small" color="primary" style={{marginLeft: "8px"}}
  //         onClick={ () => {
  //           setOpen(true);
  //         }}>
  //         <img src={D20} alt="Custom Dice Rolls"
  //           style={{
  //             width: "30px",
  //             height: "30px"
  //           }} 
  //         />
  //       </Fab>
  //     </Tooltip>
  //     { renderExtras() }
  //   </div>
  // ); 
}
